<template>
  <div class="sms-optin-page" :class="{ 'is-dialog': isDialog }">
    <img src="@/assets/heyitsme-icon.svg" class="back-btn-absolute" @click="isDialog ? $emit('close') : $router.push('/account')" alt="Home" role="button" />

    <div class="sms-optin-container">
      <button v-if="isDialog && !smsEnabled" class="sms-dialog-close-btn" type="button" @click="$emit('close')" aria-label="Close">&times;</button>

      <!-- Success confirmation -->
      <div v-if="smsEnabled" class="sms-success-state">
        <div class="sms-success-icon">✓</div>
        <h3 class="sms-success-title">Almost there!</h3>
        <p class="sms-success-message">Check your texts and reply <strong>YES</strong> to the message we just sent to finish switching to text delivery.</p>
        <button class="sms-enable-btn" @click="$emit('sms-enabled')">OK</button>
      </div>

      <!-- Opt-in form -->
      <template v-else>
      <p class="sms-optin-title">Enable SMS (Texts!)</p>
      <p class="sms-optin-subtitle">
        Now the only person who knows exactly what you need&nbsp;to&nbsp;hear&nbsp;can&nbsp;text&nbsp;you.
      </p>

      <!-- Pricing -->
      <div class="sms-pricing-card">
        <template v-if="isIncluded">
          <p class="sms-pricing-amount">Included in your plan</p>
          <p class="sms-pricing-note">No extra charge — your $5/month subscription covers both email and text. Switch back to email anytime.</p>
        </template>
        <template v-else>
          <p class="sms-pricing-amount">$5/month</p>
          <p class="sms-pricing-note">Recurring subscription billed monthly. <br>Cancel anytime from your account settings.</p>
        </template>
      </div>

      <!-- What to expect section -->
      <div class="sms-info-card">
        <h2 class="sms-info-heading">What to Expect</h2>
        <ul class="sms-info-list">
          <li>The same messages as before, at the same cadence, delivered by text instead of email.</li>
          <li>Frequency still configurable in your account settings.</li>
          <li v-if="isIncluded">Ability to switch back to email at any time.</li>
          <li v-else>Ability to switch back to email and cancel your subscription at any time.</li>
          <li>Message and data rates may apply. No partial refunds for unused days upon cancellation.</li>
        </ul>
      </div>

      <!-- Phone number input -->
      <div class="sms-stop-notice">
        <strong>Previously texted STOP?</strong> If you have ever replied STOP, STOPALL, UNSUBSCRIBE, CANCEL, END, or QUIT to <strong>+1 (484) 806-8861</strong>, you must text <strong>START</strong> to that number before continuing — otherwise the confirmation text we send after payment will not be delivered and you won't be able to complete SMS opt-in.
      </div>

      <div class="sms-form-group">
        <label for="sms-phone" class="sms-label">Mobile Phone Number</label>
        <div class="sms-phone-row">
          <span class="sms-country-code">+1</span>
          <input
            id="sms-phone"
            v-model="phoneNumber"
            type="tel"
            class="sms-phone-input"
            placeholder="5555555555"
            maxlength="10"
            autocomplete="tel"
            @input="onPhoneInput"
          />
        </div>
        <p v-if="phoneNumberError" class="sms-phone-error">{{ phoneNumberError }}</p>
        <p class="sms-phone-hint">US numbers only at this time. 10 digits, no dashes or spaces.</p>
      </div>

      <!-- TCPA consent checkbox -->
      <div class="sms-consent-group">
        <label class="sms-consent-label">
          <input type="checkbox" v-model="consentChecked" class="sms-consent-checkbox" />
          <span class="sms-consent-text">
            By checking this box and clicking <strong>{{ isIncluded ? 'Enable SMS' : 'Continue to Payment' }}</strong>, I agree to receive
            recurring text messages from <strong>Hey, It's Me</strong>, containing my saved messages,
            at the mobile number provided above.
            <br><br>Message and data rates may apply. Message frequency is based on your account settings.
            See our <router-link to="/terms" class="sms-link">Terms of Service</router-link>
            and <router-link to="/privacy-policy" class="sms-link">Privacy Policy</router-link> for details.
          </span>
        </label>
      </div>

      <!-- Enable button -->
      <button class="sms-enable-btn" :disabled="!consentChecked || phoneNumber.length !== 10 || isEnabling" @click="enableSms">
        <span v-if="!isEnabling">{{ isIncluded ? 'Enable SMS' : 'Continue to Payment' }}</span>
        <span v-else class="spinner"></span>
      </button>
      <p v-if="enableError" class="sms-phone-error">{{ enableError }}</p>

      <p class="sms-cancel-note">
        To stop receiving text messages, switch your delivery method back to "Email" in your account settings.
      </p>

      <!-- Provider disclosure (required by Twilio / CTIA) -->
      <p class="sms-provider-disclosure">
        Text messages are sent by <strong>Hey, It's Me</strong>. Powered by Twilio.
      </p>

      <button v-if="isDialog" class="sms-dialog-cancel-btn" type="button" @click="$emit('close')">Cancel</button>
      </template>
    </div>
  </div>
</template>

<script>
import { useApi } from '@/composables/useApi';

export default {
  name: 'SmsOptInPage',
  emits: ['close', 'sms-enabled'],
  props: {
    isDialog: { type: Boolean, default: false },
    isIncluded: { type: Boolean, default: false },
  },
  data() {
    return {
      api: useApi(),
      phoneNumber: '',
      consentChecked: false,
      phoneNumberError: '',
      smsEnabled: false,
      isEnabling: false,
      enableError: '',
    };
  },
  methods: {
    onPhoneInput(e) {
      // Strip anything that isn't a digit
      this.phoneNumber = e.target.value.replace(/\D/g, '').slice(0, 10);
      e.target.value = this.phoneNumber;
      this.phoneNumberError = this.phoneNumber.length > 0 && this.phoneNumber.length < 10
        ? 'Please enter a 10-digit US phone number.'
        : '';
    },
    async enableSms() {
      if (this.isEnabling) return;
      this.isEnabling = true;
      this.enableError = '';
      try {
        if (this.isIncluded) {
          await this.api.account.enableIncludedSms(this.phoneNumber);
          this.isEnabling = false;
          this.smsEnabled = true;
          return;
        }
        const result = await this.api.account.createCheckoutSession(this.phoneNumber);
        window.location.href = result.url;
      } catch (error) {
        if (error.isSessionExpired) {
          this.enableError = 'Your session has ended. Please log back in and try again.';
        } else {
          this.enableError = "We couldn't enable text messages for that number. Please double-check it and try again.";
        }
        this.isEnabling = false;
      }
    },
  },
};
</script>

<style scoped>
.sms-optin-page.is-dialog {
  min-height: auto;
  background: transparent;
  padding: 0;
}

.sms-optin-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px 40px;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  background: #fafaf8;
}

.back-btn-absolute {
  position: absolute;
  top: 20px;
  left: 20px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  opacity: 0.75;
  transition: opacity 0.2s;
}
.back-btn-absolute:hover {
  opacity: 1;
}

.sms-optin-container {
  position: relative;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.09);
  padding: 36px 40px 32px;
  max-width: 520px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sms-optin-title {
  color: #312d30;
  margin: 0 0 4px;
  font-size:2.5rem; 
  text-align:center; 
  font-family:'StayWonderful';
}

.sms-optin-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  text-align: center;
  line-height: 1.5;
}

.sms-pricing-card {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0;
  background: #f0f7f0;
  border: 2px solid #7da47e;
  border-radius: 12px;
  padding: 18px 24px 14px;
  text-align: center;
  flex-direction: column;
}

.sms-pricing-amount {
  font-size: 1.5rem;
  font-weight: 500;
  color: #388E3C;
  line-height: 1;
  text-align: center;
}

.sms-pricing-period {
  font-size: 1.1rem;
  font-weight: 600;
  color: #6b7280;
  display: inline;
}

.sms-pricing-note {
  font-size: 0.85rem;
  color: #9ca3af;
  margin: 8px 0 0;
  width: 100%;
}

.sms-info-card {
  background: #f0f7f0;
  border-left: 4px solid #7da47e;
  border-radius: 8px;
  padding: 16px 20px;
}

.sms-info-heading {
  font-size: 1rem;
  font-weight: 700;
  color: #312d30;
  margin: 0 0 10px;
}

.sms-info-list {
  margin: 0;
  padding-left: 20px;
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1.7;
  text-align:left;
}

.sms-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sms-label {
  font-weight: 700;
  font-size: 1rem;
  color: #312d30;
}

.sms-phone-row {
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.sms-country-code {
  padding: 10px 12px;
  background: #f3f4f6;
  color: #374151;
  font-size: 1rem;
  font-weight: 600;
  border-right: 1px solid #ccc;
  white-space: nowrap;
}

.sms-phone-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 12px;
  font-size: 16px;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  color: #312d30;
  background: transparent;
  width: 100%;
  box-sizing: border-box;
}

.sms-stop-notice {
  background: #fef9ec;
  border: 1px solid #f5c842;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 0.9rem;
  color: #7a6000;
  margin-bottom: 16px;
  line-height: 1.5;
}

.sms-phone-hint {
  font-size: 0.85rem;
  color: #9ca3af;
}

.sms-phone-error {
  font-size: 0.85rem;
  color: #d32f2f;
  margin: 0;
}

.sms-consent-group {
  background: #f9f9f7;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
}

.sms-consent-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
}

.sms-consent-checkbox {
  margin-top: 3px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  accent-color: #7da47e;
  cursor: pointer;
}

.sms-consent-text {
  font-size: 0.88rem;
  color: #4b5563;
  line-height: 1.6;
}

.sms-link {
  color: #7da47e;
  text-decoration: underline;
  font-weight: 600;
}
.sms-link:hover {
  color: #388E3C;
}

.sms-enable-btn {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  width: 100%;
}
.sms-enable-btn:hover:not(:disabled) {
  background: #388E3C;
}
.sms-enable-btn:disabled {
  background: #b5cbb2;
  cursor: not-allowed;
  opacity: 0.7;
}

.sms-cancel-note {
  font-size: 0.87rem;
  color: #9ca3af;
  margin: 0;
  text-align: center;
  line-height: 1.5;
}

.sms-provider-disclosure {
  font-size: 0.82rem;
  color: #b0b5bb;
  margin: 0;
  text-align: center;
  line-height: 1.5;
  border-top: 1px solid #f0f0ee;
  padding-top: 12px;
}

.sms-dialog-close-btn {
  position: absolute;
  top: 14px;
  right: 16px;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.sms-dialog-close-btn:hover {
  color: #312d30;
}

.sms-dialog-cancel-btn {
  width: 100%;
  padding: 10px;
  border: 2px solid #ccc;
  border-radius: 8px;
  background: #fff;
  color: #6b7280;
  font-size: 0.95rem;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.sms-dialog-cancel-btn:hover {
  border-color: #312d30;
  color: #312d30;
}

.sms-success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  text-align: center;
}

.sms-success-icon {
  width: 56px;
  height: 56px;
  background-color: #4caf50;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: bold;
}

.sms-success-title {
  font-size: 1.4rem;
  color: #312d30;
  margin: 0;
}

.sms-success-message {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 560px) {
  .sms-optin-container {
    padding: 24px 18px 24px;
  }
  .sms-optin-title {
    font-size: 1.3rem;
  }
}
</style>
